import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatIDR } from "../lib/api";
import { Building2, MapPin, Phone, Factory, CalendarDays, Tag } from "lucide-react";

function fmtDate(s) {
    if (!s) return "-";
    try {
        return new Date(s).toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
    } catch {
        return s;
    }
}

function Row({ label, value, testid }) {
    return (
        <div className="flex justify-between gap-4 py-2 border-b border-[#E9EFEA] last:border-0">
            <span className="text-sm text-[#4A5E57]">{label}</span>
            <span className="text-sm font-medium text-right" data-testid={testid}>
                {value}
            </span>
        </div>
    );
}

export default function AssetPublicDetail() {
    const { id } = useParams();
    const [asset, setAsset] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/assets/${id}`)
            .then((res) => setAsset(res.data))
            .catch(() => setError("Aset tidak ditemukan"))
            .finally(() => setLoading(false));
    }, [id]);

    return (
        <div className="min-h-screen bg-[#F7F9F6] text-[#1A362D] py-8 px-4">
            <div className="max-w-lg mx-auto" data-testid="asset-public-page">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-md bg-[#245B4B] text-white flex items-center justify-center font-heading font-semibold">
                        RS
                    </div>
                    <div>
                        <div className="font-heading font-semibold leading-tight">
                            Rumah Sakit Mulya
                        </div>
                        <div className="text-xs text-[#6A9C89] uppercase tracking-widest">
                            Detail Aset
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="surface-card p-8 text-center text-[#4A5E57]">Memuat...</div>
                ) : error ? (
                    <div
                        className="surface-card p-8 text-center text-[#B3432B]"
                        data-testid="asset-public-error"
                    >
                        {error}
                    </div>
                ) : (
                    <div className="surface-card overflow-hidden">
                        <div className="bg-[#245B4B] text-white p-6">
                            <div className="text-xs uppercase tracking-widest text-[#C4DAD2] mb-1">
                                {asset.asset_type || "Aset Tetap"}
                            </div>
                            <h1
                                className="font-heading text-2xl font-semibold"
                                data-testid="asset-public-name"
                            >
                                {asset.name}
                            </h1>
                            <div className="text-sm text-[#C4DAD2] mt-1" data-testid="asset-public-code">
                                No. Aset: {asset.code}
                            </div>
                        </div>
                        <div className="p-6 space-y-5">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div className="flex items-center gap-2 text-[#4A5E57]">
                                    <MapPin size={15} className="text-[#245B4B] shrink-0" />
                                    <span data-testid="asset-public-location">
                                        {asset.location || "-"}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 text-[#4A5E57]">
                                    <Building2 size={15} className="text-[#245B4B] shrink-0" />
                                    <span>{asset.unit_name || "-"}</span>
                                </div>
                                <div className="flex items-center gap-2 text-[#4A5E57]">
                                    <Factory size={15} className="text-[#245B4B] shrink-0" />
                                    <span data-testid="asset-public-vendor">
                                        {asset.vendor_name || "-"}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 text-[#4A5E57]">
                                    <Phone size={15} className="text-[#245B4B] shrink-0" />
                                    <span>{asset.vendor_contact || "-"}</span>
                                </div>
                                <div className="flex items-center gap-2 text-[#4A5E57]">
                                    <CalendarDays size={15} className="text-[#245B4B] shrink-0" />
                                    <span>Pakai: {fmtDate(asset.first_use_date)}</span>
                                </div>
                                <div className="flex items-center gap-2 text-[#4A5E57]">
                                    <Tag size={15} className="text-[#245B4B] shrink-0" />
                                    <span>{asset.tangible ? "Berwujud" : "Tidak Berwujud"}</span>
                                </div>
                            </div>

                            <div>
                                <div className="label-uppercase mb-2">Informasi Aset</div>
                                <Row label="Merk" value={asset.brand || "-"} testid="asset-public-brand" />
                                <Row
                                    label="Serial Number"
                                    value={asset.serial_number || "-"}
                                    testid="asset-public-serial"
                                />
                            </div>

                            <div>
                                <div className="label-uppercase mb-2">Informasi Nilai</div>
                                <Row label="Harga Perolehan" value={formatIDR(asset.purchase_price)} />
                                <Row
                                    label="Nilai Aset Saat Ini"
                                    value={formatIDR(asset.current_value)}
                                    testid="asset-public-value"
                                />
                                <Row
                                    label="Penyusutan / bulan"
                                    value={formatIDR(asset.monthly_depreciation)}
                                />
                                <Row
                                    label="Masa Manfaat"
                                    value={`${asset.useful_life_months || 0} bulan`}
                                />
                                <Row label="Sisa Umur" value={`${asset.remaining_life || 0} bulan`} />
                                <Row
                                    label="Tarif Penyusutan"
                                    value={`${asset.depreciation_rate || 0}% / tahun`}
                                />
                                <Row
                                    label="Distribusikan ke"
                                    value={asset.distribute_to || "-"}
                                />
                            </div>

                            {asset.notes && (
                                <div>
                                    <div className="label-uppercase mb-2">Keterangan</div>
                                    <p className="text-sm text-[#4A5E57]" data-testid="asset-public-notes">
                                        {asset.notes}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
